import { useEffect, useState, useCallback } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { X, Download, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react'
import { Button } from '@/components/ui/button'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString()

interface CertificateViewerModalProps {
  /** Blob URL created from the streamed certificate bytes */
  blobUrl: string
  /** MIME type of the blob — determines whether to render as image or PDF */
  contentType: string
  title: string
  onClose: () => void
}

export function CertificateViewerModal({ blobUrl, contentType, title, onClose }: CertificateViewerModalProps) {
  const isPdf = contentType === 'application/pdf'

  const [numPages, setNumPages] = useState(0)
  const [page, setPage] = useState(1)
  const [scale, setScale] = useState(1.2)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (isPdf && e.key === 'ArrowRight') setPage(p => Math.min(p + 1, numPages))
      if (isPdf && e.key === 'ArrowLeft')  setPage(p => Math.max(p - 1, 1))
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose, numPages, isPdf])

  const onDocumentLoad = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setPage(1)
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-900/90 backdrop-blur-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 bg-slate-900 border-b border-white/10 shrink-0">
        <span className="text-white text-sm font-medium truncate max-w-md">{title}</span>

        <div className="flex items-center gap-1">
          {isPdf && (
            <>
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
                onClick={() => setScale(s => Math.max(0.5, s - 0.2))}>
                <ZoomOut size={15} />
              </Button>
              <span className="text-slate-400 text-xs w-10 text-center">{Math.round(scale * 100)}%</span>
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
                onClick={() => setScale(s => Math.min(3, s + 0.2))}>
                <ZoomIn size={15} />
              </Button>
              <div className="w-px h-5 bg-white/10 mx-2" />
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
                onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page <= 1}>
                <ChevronLeft size={15} />
              </Button>
              <span className="text-slate-400 text-xs w-16 text-center">{page} / {numPages}</span>
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
                onClick={() => setPage(p => Math.min(p + 1, numPages))} disabled={page >= numPages}>
                <ChevronRight size={15} />
              </Button>
              <div className="w-px h-5 bg-white/10 mx-2" />
            </>
          )}

          <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-white/10 gap-1.5 h-8" asChild>
            <a href={blobUrl} download={title} target="_blank" rel="noreferrer">
              <Download size={14} /> Download
            </a>
          </Button>

          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-white/10 h-8 w-8 p-0 ml-1"
            onClick={onClose}>
            <X size={16} />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto flex justify-center items-start py-8 px-4">
        {isPdf ? (
          <Document
            file={blobUrl}
            onLoadSuccess={onDocumentLoad}
            loading={<p className="text-slate-400 mt-20">Loading…</p>}
            error={<p className="text-red-400 mt-20">Failed to load PDF.</p>}
          >
            <Page pageNumber={page} scale={scale} className="shadow-2xl" loading="" />
          </Document>
        ) : (
          <img
            src={blobUrl}
            alt={title}
            className="max-w-full shadow-2xl rounded"
            style={{ maxHeight: 'calc(100vh - 120px)' }}
          />
        )}
      </div>
    </div>
  )
}
